import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { AlertTriangle, DollarSign, TrendingDown, Scale } from 'lucide-react';
import { formatCurrency } from '../../lib/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PortfolioDefaultMetrics = ({ getAuthHeaders }) => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/default-recovery/portfolio-metrics`, { headers: getAuthHeaders() });
        setMetrics(res.data);
      } catch (err) {
        console.error('Failed to load default metrics:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [getAuthHeaders]);

  if (loading || !metrics) return null;
  if (metrics.total_defaulted_deals === 0) return null;

  return (
    <Card className="mb-6" data-testid="portfolio-default-metrics">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-red-500" />
          Default & Recovery
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <div data-testid="metric-defaulted-deals">
            <p className="text-xs text-muted-foreground">Defaulted Deals</p>
            <p className="text-xl font-bold text-red-600">{metrics.total_defaulted_deals}</p>
          </div>
          <div data-testid="metric-defaulted-exposure">
            <p className="text-xs text-muted-foreground">Total Exposure</p>
            <p className="text-xl font-bold text-red-600">{formatCurrency(metrics.total_defaulted_exposure)}</p>
          </div>
          <div data-testid="metric-gross-recovered">
            <p className="text-xs text-muted-foreground">Gross Recovered</p>
            <p className="text-xl font-bold text-green-600">{formatCurrency(metrics.total_gross_recovered)}</p>
            {(metrics.total_lawyer_recovered != null || metrics.total_in_house_recovered != null) && (
              <p className="text-[10px] text-muted-foreground">
                lawyer {formatCurrency(metrics.total_lawyer_recovered || 0)} · in-house {formatCurrency(metrics.total_in_house_recovered || 0)}
              </p>
            )}
          </div>
          <div data-testid="metric-write-offs">
            <p className="text-xs text-muted-foreground">Write-Offs</p>
            <p className="text-xl font-bold text-orange-600">{formatCurrency(metrics.total_write_offs)}</p>
          </div>
          <div data-testid="metric-net-exposure">
            <p className="text-xs text-muted-foreground">Net Exposure</p>
            <p className="text-xl font-bold">{formatCurrency(metrics.net_exposure)}</p>
          </div>
          <div data-testid="metric-recovery-rate">
            <p className="text-xs text-muted-foreground">Recovery Rate</p>
            <p className="text-xl font-bold text-blue-600">{metrics.recovery_rate}%</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PortfolioDefaultMetrics;
